import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  SaveIcon, 
  MapIcon, 
  SwitchHorizontalIcon, 
  OfficeBuildingIcon,
  PlusIcon,
  TrashIcon,
  GlobeIcon
} from '@heroicons/react/outline';
import { 
  getBatasGampong, 
  getOrbitasi, 
  getPendudukDusun,
  addGeographyData,
  updateGeographyData,
  deleteGeographyData
} from 'services/apiRoutes';

export default function CMSGeographyPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [batas, setBatas] = useState([]);
  const [orbitasi, setOrbitasi] = useState([]);
  const [pendudukDusun, setPendudukDusun] = useState([]);

  useEffect(() => {
    fetchData();
  }, [slug]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resBatas, resOrbit, resDusun] = await Promise.all([
        getBatasGampong(slug),
        getOrbitasi(slug),
        getPendudukDusun(slug)
      ]);
      
      setBatas(resBatas.error ? [] : (resBatas.data?.data || []));
      setOrbitasi(resOrbit.error ? [] : (resOrbit.data?.data || []));
      setPendudukDusun(resDusun.error ? [] : (resDusun.data?.data || []));
    } catch (error) {
      console.error("Error fetching geography data:", error);
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (coll, data) => {
    try {
      const res = await addGeographyData(slug, coll, data);
      if (res.error) throw new Error(res.message);
      toast.success("Item berhasil ditambah");
      fetchData();
    } catch (error) {
      console.error("error add", error);
      toast.error("Gagal menambah data");
    }
  };

  const handleUpdateItem = async (coll, id, data) => {
    try {
      const res = await updateGeographyData(slug, coll, id, data);
      if (res.error) throw new Error(res.message);
      // No toast for silent updates (onBlur)
    } catch (error) {
      console.error("error update", error);
      toast.error("Gagal memperbarui data");
    }
  };

  const handleDeleteItem = async (coll, id) => {
    if (!window.confirm("Hapus item ini?")) return;
    try {
      const res = await deleteGeographyData(slug, coll, id);
      if (res.error) throw new Error(res.message);
      toast.success("Item berhasil dihapus");
      fetchData();
    } catch (error) {
      console.error("error delete", error);
      toast.error("Gagal menghapus data");
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading data Geografis...</div>;

  return (
    <div className="space-y-12 animate-fadeIn pb-20">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight text-premium">Manajemen Geografis & Wilayah</h2>
          <p className="mt-1 text-sm text-gray-500">Kelola batas desa, data dusun, orbitasi, dan koordinat peta.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-12">
        {/* Batas Gampong */}
        <section className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm space-y-8 transition-all duration-300 hover:shadow-xl hover:shadow-gray-200/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-50 rounded-2xl text-blue-500">
                <SwitchHorizontalIcon className="w-7 h-7" />
              </div>
              <div>
                <h3 className="font-extrabold text-gray-900 text-lg">Batas Gampong</h3>
                <p className="text-xs text-gray-400 font-medium">Informasi perbatasan wilayah desa</p>
              </div>
            </div>
            <button 
              onClick={() => handleAddItem('batas_gampong', { nama: "", deskripsi: "" })}
              className="p-2.5 bg-primary/5 text-primary hover:bg-primary hover:text-white rounded-xl transition-all duration-300 transform hover:rotate-90"
            >
              <PlusIcon className="w-6 h-6" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {batas.map((item) => (
              <div key={item.id} className="p-6 bg-gray-50/30 rounded-3xl border border-transparent hover:border-blue-100 hover:bg-white transition-all duration-300 group relative">
                <button 
                  onClick={() => handleDeleteItem('batas_gampong', item.id)}
                  className="absolute top-4 right-4 p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Arah Mata Angin</label>
                    <input 
                      type="text"
                      placeholder="Contoh: Utara"
                      defaultValue={item.nama}
                      onBlur={(e) => handleUpdateItem('batas_gampong', item.id, { nama: e.target.value })}
                      className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-sm font-extrabold text-gray-700 focus:border-primary focus:ring-4 focus:ring-primary/5 focus:outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Berbatasan Dengan</label>
                    <textarea 
                      placeholder="Sebutkan desa/wilayah terkait..."
                      defaultValue={item.deskripsi}
                      onBlur={(e) => handleUpdateItem('batas_gampong', item.id, { deskripsi: e.target.value })}
                      className="w-full px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-sm font-medium text-gray-600 focus:border-primary focus:ring-4 focus:ring-primary/5 focus:outline-none transition-all min-h-[80px]"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Orbitasi */}
        <section className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm space-y-8 transition-all duration-300 hover:shadow-xl hover:shadow-gray-200/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-orange-50 rounded-2xl text-orange-500">
                <GlobeIcon className="w-7 h-7" />
              </div>
              <div>
                <h3 className="font-extrabold text-gray-900 text-lg">Orbitasi</h3>
                <p className="text-xs text-gray-400 font-medium">Jarak dan waktu tempuh ke pusat kota</p>
              </div>
            </div>
            <button 
              onClick={() => handleAddItem('orbitasi', { nama: "", deskripsi: "" })}
              className="p-2.5 bg-primary/5 text-primary hover:bg-primary hover:text-white rounded-xl transition-all duration-300 transform hover:rotate-90"
            >
              <PlusIcon className="w-6 h-6" />
            </button>
          </div>
          <div className="overflow-hidden rounded-3xl border border-gray-100">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/50">
                <tr className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  <th className="px-6 py-4">Tujuan / Lokasi</th>
                  <th className="px-6 py-4">Jarak & Waktu Tempuh</th>
                  <th className="px-6 py-4 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 bg-white">
                {orbitasi.map((item) => (
                  <tr key={item.id} className="group hover:bg-orange-50/5 transition-colors">
                    <td className="px-6 py-4">
                      <input 
                        type="text"
                        defaultValue={item.nama}
                        placeholder="Contoh: Ibu Kota Kecamatan"
                        onBlur={(e) => handleUpdateItem('orbitasi', item.id, { nama: e.target.value })}
                        className="w-full bg-transparent border-none text-sm font-bold text-gray-700 focus:ring-0 placeholder:text-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <input 
                        type="text"
                        defaultValue={item.deskripsi}
                        placeholder="Contoh: 5 Km / 15 Menit"
                        onBlur={(e) => handleUpdateItem('orbitasi', item.id, { deskripsi: e.target.value })}
                        className="w-full bg-transparent border-none text-sm font-medium text-gray-600 focus:ring-0 placeholder:text-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button 
                        onClick={() => handleDeleteItem('orbitasi', item.id)}
                        className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Demografi Per Dusun */}
        <section className="bg-white p-8 rounded-[2rem] border border-gray-100 shadow-sm space-y-8 transition-all duration-300 hover:shadow-xl hover:shadow-gray-200/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-green-50 rounded-2xl text-green-500">
                <OfficeBuildingIcon className="w-7 h-7" />
              </div>
              <div>
                <h3 className="font-extrabold text-gray-900 text-lg">Demografi Per Dusun</h3>
                <p className="text-xs text-gray-400 font-medium">Data kependudukan berdasarkan wilayah dusun</p>
              </div>
            </div>
            <button 
              onClick={() => handleAddItem('penduduk_dusun', { namaDusun: "", jumlahKK: "0", jumlahPenduduk: "0" })}
              className="p-2.5 bg-primary/5 text-primary hover:bg-primary hover:text-white rounded-xl transition-all duration-300 transform hover:rotate-90"
            >
              <PlusIcon className="w-6 h-6" />
            </button>
          </div>
          <div className="overflow-hidden rounded-3xl border border-gray-100">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50/50">
                <tr className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  <th className="px-6 py-4">Nama Dusun</th>
                  <th className="px-6 py-4">Jumlah KK</th>
                  <th className="px-6 py-4">Jumlah Penduduk</th>
                  <th className="px-6 py-4 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 bg-white">
                {pendudukDusun.map((item) => (
                  <tr key={item.id} className="group hover:bg-green-50/5 transition-colors">
                    <td className="px-6 py-4">
                      <input 
                        type="text"
                        defaultValue={item.namaDusun}
                        placeholder="Nama Dusun"
                        onBlur={(e) => handleUpdateItem('penduduk_dusun', item.id, { namaDusun: e.target.value })}
                        className="w-full bg-transparent border-none text-sm font-black text-gray-700 focus:ring-0"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <input 
                          type="number"
                          defaultValue={item.jumlahKK}
                          onBlur={(e) => handleUpdateItem('penduduk_dusun', item.id, { jumlahKK: e.target.value })}
                          className="w-24 bg-transparent border-b border-transparent group-hover:border-gray-100 text-sm font-bold text-gray-600 focus:ring-0 focus:border-primary transition-all"
                        />
                        <span className="text-[10px] text-gray-300 font-bold uppercase">KK</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <input 
                          type="number"
                          defaultValue={item.jumlahPenduduk}
                          onBlur={(e) => handleUpdateItem('penduduk_dusun', item.id, { jumlahPenduduk: e.target.value })}
                          className="w-24 bg-transparent border-b border-transparent group-hover:border-gray-100 text-sm font-black text-primary focus:ring-0 focus:border-primary transition-all"
                        />
                        <span className="text-[10px] text-primary/30 font-bold uppercase">Jiwa</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button 
                        onClick={() => handleDeleteItem('penduduk_dusun', item.id)}
                        className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <style>{`
        .text-premium {
          background: linear-gradient(135deg, #0f172a 0%, #334155 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
}
